import moment from 'moment';
import { AFFORDABLE_PROGRAMS } from '../../constants/affordableProgramNames';
import HUD_SUBSIDY_CODES from '../../constants/hudSubsidyCodes';
import { TAB_KEYS } from './constants';

const getHudFloorPlanCode = (unit?: Object): ?string =>
  (unit?.floorPlan?.floorPlanAffordablePrograms ?? []).find(
    (fp) =>
      fp?.propertyAffordableProgram?.masterAffordableProgram?.name ===
      AFFORDABLE_PROGRAMS.HUD,
  )?.hudCode;

const getHudPrimarySubsidyCode = (selectedProperty?: Object): string =>
  selectedProperty?.setup?.hudPrimarySubsidyType?.code || '';

export const checkIsSection236 = ({
  shouldCheck,
  section236Flag,
  unit,
  selectedProperty,
}: {
  shouldCheck: boolean,
  section236Flag: boolean,
  unit?: Object,
  selectedProperty?: Object,
}): boolean => {
  if (!shouldCheck || !section236Flag) {
    return false;
  }

  const fpHudCode = getHudFloorPlanCode(unit);
  const hudPrimarySubsidy = getHudPrimarySubsidyCode(selectedProperty);

  // The secondary 236 code is deliberately not checked: it marks a household
  // that receives Section 8/RAP/Rent Supplement inside a 236 property, so it
  // has assistance to terminate. Only 236 as the property's own program, or on
  // the unit's floor plan, means there is none.
  return (
    hudPrimarySubsidy === HUD_SUBSIDY_CODES.SECTION_236_PRIMARY ||
    fpHudCode === HUD_SUBSIDY_CODES.SECTION_236_PRIMARY
  );
};

// BMIR as the property's own program or on the unit's floor plan means the
// household has no assistance payment to terminate. The secondary BMIR marker
// ('B') is not checked, for the same reason as Section 236 above.
export const checkIsBmir = ({
  unit,
  selectedProperty,
}: {
  unit?: Object,
  selectedProperty?: Object,
}): boolean => {
  const fpHudCode = getHudFloorPlanCode(unit);
  const hudPrimarySubsidy = getHudPrimarySubsidyCode(selectedProperty);

  return (
    hudPrimarySubsidy === HUD_SUBSIDY_CODES.BMIR_PRIMARY ||
    fpHudCode === HUD_SUBSIDY_CODES.BMIR_PRIMARY
  );
};

export const getActiveResidentProfileTab = (requestedTab?: mixed): string => {
  const normalizedTab = Array.isArray(requestedTab)
    ? requestedTab[0]
    : requestedTab;

  if (typeof normalizedTab !== 'string') {
    return TAB_KEYS.LEDGER;
  }

  return normalizedTab === TAB_KEYS.QUALIFICATION_HISTORY
    ? TAB_KEYS.LEDGER
    : normalizedTab || TAB_KEYS.LEDGER;
};

/**
 * A resident's affordable qualification tabs come from the programs on their
 * unit's floor plan, so they change whenever the floor plan changes. A link to
 * a program tab that has since been removed (a bookmark, a shared URL, browser
 * history) still carries the old tab key, and that key matches nothing on the
 * profile, leaving the page with no tab selected and the qualification
 * unreachable. When the selected key looks like a program tab but is not in the
 * list the profile actually built, use the only current program when there is
 * no ambiguity. Otherwise, fall back to the ledger.
 */
export const resolveSelectedResidentTab = (
  selectedTab: string,
  availableAffordableTabKeys: Array<string> = [],
  knownStaticTabKeys: Array<string> = Object.keys(TAB_KEYS).map(
    (tabName) => TAB_KEYS[tabName],
  ),
): string => {
  if (!selectedTab) {
    return TAB_KEYS.LEDGER;
  }

  if (knownStaticTabKeys.includes(selectedTab)) {
    return selectedTab;
  }

  if (availableAffordableTabKeys.includes(selectedTab)) {
    return selectedTab;
  }

  return availableAffordableTabKeys.length === 1
    ? availableAffordableTabKeys[0]
    : TAB_KEYS.LEDGER;
};

export const filterAndDeleteShowPropFromTabs = (tabs: Array<Object>) =>
  tabs
    .filter((tab) => !tab.hasOwnProperty('show') || tab.show !== false)
    .map((tab) => {
      delete tab.show;
      return tab;
    });

const HUD_RECERT_MOVE_OUT_GRACE_MONTHS = 3;

export const isHudRecertOverdueForMoveOut = (
  moveOutDate: ?(string | Date | Object),
  hudRecertDueDate: ?(string | Date | Object),
): boolean => {
  if (!moveOutDate || !hudRecertDueDate) {
    return false;
  }

  const moveOut = moment(moveOutDate);
  const threshold = moment(hudRecertDueDate).add(
    HUD_RECERT_MOVE_OUT_GRACE_MONTHS,
    'months',
  );

  if (!moveOut.isValid() || !threshold.isValid()) {
    return false;
  }

  return moveOut.isAfter(threshold, 'day');
};
